import { useState, useRef, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { DuplicateWithDetails, ContactDetails, EmailDetails, PhoneDetails, AddressDetails } from '@/types/duplicate';

type MatchRule = 'shared_email' | 'exact_name_email' | 'exact_name' | 'shared_phone' | 'all';
type KeepPreference = 'most_data' | 'oldest' | 'newest';
type DialogState = 'configure' | 'executing' | 'complete';

interface MergeLogEntry {
  keepName: string;
  discardName: string;
  status: 'success' | 'error' | 'skipped';
  error?: string;
}

interface AutoMergeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  duplicates: DuplicateWithDetails[];
  onComplete: () => void;
}

const MATCH_RULE_OPTIONS: { value: MatchRule; label: string; description: string }[] = [
  { value: 'shared_email', label: 'Shared Email', description: 'Safest, merges only contacts sharing an email' },
  { value: 'exact_name_email', label: 'Name + Email', description: 'Same first/last name AND shared email' },
  { value: 'exact_name', label: 'Exact Name', description: 'Same first and last name (may false-match common names)' },
  { value: 'shared_phone', label: 'Shared Phone', description: 'Same phone number' },
  { value: 'all', label: 'All Rules', description: 'Merge all detected duplicates (least precise)' },
];

const KEEP_PREFERENCE_OPTIONS: { value: KeepPreference; label: string; description: string }[] = [
  { value: 'most_data', label: 'Most complete contact', description: 'Smart fields (type, rank, status, relationship) and name from the contact with the most data' },
  { value: 'oldest', label: 'Oldest contact', description: 'Smart fields and name from the contact created first' },
  { value: 'newest', label: 'Newest contact', description: 'Smart fields and name from the contact created most recently' },
];

function scoreContact(
  contact: ContactDetails,
  emails: EmailDetails[],
  phones: PhoneDetails[],
  addresses: AddressDetails[]
): number {
  let score = emails.length + phones.length + addresses.length;
  if (contact.company_name) score++;
  if (contact.job_title) score++;
  if (contact.type) score++;
  if (contact.rank) score++;
  if (contact.relationship) score++;
  return score;
}

function getContactDisplayName(contact: ContactDetails): string {
  if (contact.is_company) {
    return contact.company_name || 'Unknown Company';
  }
  return `${contact.first_name} ${contact.last_name}`.trim() || 'Unknown';
}

export function AutoMergeDialog({
  open,
  onOpenChange,
  // workspaceId is accepted for future use (e.g., cache invalidation)
  workspaceId: _workspaceId,
  duplicates,
  onComplete,
}: AutoMergeDialogProps) {
  const [state, setState] = useState<DialogState>('configure');
  const [matchRule, setMatchRule] = useState<MatchRule>('shared_email');
  const [keepPreference, setKeepPreference] = useState<KeepPreference>('most_data');
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [log, setLog] = useState<MergeLogEntry[]>([]);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const cancelledRef = useRef(false);

  const filteredPairs = useMemo(() => {
    if (matchRule === 'all') return duplicates;
    return duplicates.filter(d => d.duplicate.match_type === matchRule);
  }, [duplicates, matchRule]);

  const determineKeeper = useCallback((
    dup: DuplicateWithDetails
  ): { keeper: ContactDetails; discard: ContactDetails; keeperEmails: EmailDetails[]; keeperPhones: PhoneDetails[] } => {
    const c1 = dup.contact1;
    const c2 = dup.contact2;

    let keeperIsC1: boolean;

    if (keepPreference === 'most_data') {
      const score1 = scoreContact(c1, dup.emails1, dup.phones1, dup.addresses1 || []);
      const score2 = scoreContact(c2, dup.emails2, dup.phones2, dup.addresses2 || []);
      if (score1 !== score2) {
        keeperIsC1 = score1 >= score2;
      } else {
        // Tie-break: older created_at
        const d1 = c1.created_at ? new Date(c1.created_at).getTime() : Infinity;
        const d2 = c2.created_at ? new Date(c2.created_at).getTime() : Infinity;
        keeperIsC1 = d1 <= d2;
      }
    } else if (keepPreference === 'oldest') {
      const d1 = c1.created_at ? new Date(c1.created_at).getTime() : Infinity;
      const d2 = c2.created_at ? new Date(c2.created_at).getTime() : Infinity;
      keeperIsC1 = d1 <= d2;
    } else {
      // newest
      const d1 = c1.created_at ? new Date(c1.created_at).getTime() : 0;
      const d2 = c2.created_at ? new Date(c2.created_at).getTime() : 0;
      keeperIsC1 = d1 >= d2;
    }

    if (keeperIsC1) {
      return { keeper: c1, discard: c2, keeperEmails: dup.emails1, keeperPhones: dup.phones1 };
    } else {
      return { keeper: c2, discard: c1, keeperEmails: dup.emails2, keeperPhones: dup.phones2 };
    }
  }, [keepPreference]);

  const handleStart = async () => {
    if (filteredPairs.length === 0) return;

    cancelledRef.current = false;
    setState('executing');
    setProgress(0);
    setTotal(filteredPairs.length);
    setLog([]);
    setSuccessCount(0);
    setErrorCount(0);

    let successes = 0;
    let errors = 0;

    // Track contacts already merged (soft-deleted) in this batch
    // so we skip pairs where either contact was already processed
    const mergedContactIds = new Set<string>();

    for (let i = 0; i < filteredPairs.length; i++) {
      if (cancelledRef.current) break;

      const dup = filteredPairs[i];

      // Skip if either contact was already merged in this batch
      if (mergedContactIds.has(dup.contact1.id) || mergedContactIds.has(dup.contact2.id)) {
        setProgress(i + 1);
        setLog(prev => [...prev, {
          keepName: getContactDisplayName(dup.contact1),
          discardName: getContactDisplayName(dup.contact2),
          status: 'skipped' as const,
          error: 'One or both contacts already merged',
        }]);
        continue;
      }

      const { keeper, discard, keeperEmails, keeperPhones } = determineKeeper(dup);

      const keeperName = getContactDisplayName(keeper);
      const discardName = getContactDisplayName(discard);

      // Quick check: verify both contacts still exist before attempting merge
      const { data: existCheck } = await supabase
        .from('contacts')
        .select('id')
        .in('id', [keeper.id, discard.id])
        .is('deleted_at', null);

      if (!existCheck || existCheck.length < 2) {
        mergedContactIds.add(keeper.id);
        mergedContactIds.add(discard.id);
        setProgress(i + 1);
        setLog(prev => [...prev, {
          keepName: keeperName,
          discardName: discardName,
          status: 'skipped' as const,
          error: 'Contact already deleted or merged',
        }]);
        continue;
      }

      // Collect ALL emails and phones from both contacts to preserve everything
      const allEmails = [...new Set([...dup.emails1, ...dup.emails2].map(e => e.email))];
      const allPhones = [...new Set([...dup.phones1, ...dup.phones2].map(p => p.phone_number))];

      const primaryEmail = keeperEmails.find(e => e.is_primary)?.email
        || keeperEmails[0]?.email
        || null;
      const primaryPhone = keeperPhones.find(p => p.isPrimary)?.phone_number
        || keeperPhones[0]?.phone_number
        || null;

      try {
        const { error } = await supabase.rpc('merge_contacts_v2', {
          keep_id: keeper.id,
          discard_id: discard.id,
          primary_email: primaryEmail,
          primary_phone: primaryPhone,
          options: {
            keep_emails: allEmails,
            keep_phones: allPhones,
          },
        });

        if (error) throw error;

        // Track the discarded contact so future pairs skip it
        mergedContactIds.add(discard.id);

        successes++;
        setSuccessCount(successes);
        setLog(prev => [...prev, { keepName: keeperName, discardName: discardName, status: 'success' }]);
      } catch (err: any) {
        errors++;
        setErrorCount(errors);
        setLog(prev => [...prev, {
          keepName: keeperName,
          discardName: discardName,
          status: 'error',
          error: err?.message || 'Unknown error',
        }]);
      }

      setProgress(i + 1);
    }

    setState('complete');
  };

  const handleCancel = () => {
    if (state === 'executing') {
      cancelledRef.current = true;
    } else {
      resetAndClose();
    }
  };

  const resetAndClose = () => {
    setState('configure');
    setProgress(0);
    setTotal(0);
    setLog([]);
    setSuccessCount(0);
    setErrorCount(0);
    cancelledRef.current = false;
    onOpenChange(false);
  };

  const handleClose = () => {
    if (state === 'complete') {
      onComplete();
    }
    resetAndClose();
  };

  const handleViewMergeHistory = () => {
    onComplete();
    resetAndClose();
    // Navigate to merge history tab - the parent page handles tab switching
    const tabTrigger = document.querySelector('[data-value="history"]') as HTMLElement;
    if (tabTrigger) tabTrigger.click();
  };

  const progressPercent = total > 0 ? (progress / total) * 100 : 0;

  // Visible log entries (last 5)
  const visibleLog = log.slice(-5);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          if (state === 'executing') {
            cancelledRef.current = true;
          } else {
            handleClose();
          }
        }
      }}
    >
      <DialogContent className="max-w-lg">
        {state === 'configure' && (
          <>
            <DialogHeader>
              <DialogTitle>Auto-Merge Duplicates</DialogTitle>
              <DialogDescription>
                Found {duplicates.length} duplicate pair{duplicates.length !== 1 ? 's' : ''} in this workspace.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 py-4">
              {/* Match Rule */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Match Rule</label>
                <Select value={matchRule} onValueChange={(v) => setMatchRule(v as MatchRule)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MATCH_RULE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {MATCH_RULE_OPTIONS.find(o => o.value === matchRule)?.description}
                </p>
              </div>

              {/* Keep Preference */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Keep smart fields from...</label>
                <p className="text-xs text-muted-foreground">Name, company, job title, type, rank, status, and relationship come from the kept contact. All emails, phones, addresses, notes, groups, and history are combined from both.</p>
                <Select value={keepPreference} onValueChange={(v) => setKeepPreference(v as KeepPreference)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KEEP_PREFERENCE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Preview */}
              <div className="rounded-md border p-3 space-y-1">
                <p className="text-sm font-medium">
                  {filteredPairs.length} pair{filteredPairs.length !== 1 ? 's' : ''} match your rule
                </p>
                <p className="text-xs text-muted-foreground">
                  All emails, phones, addresses, notes, groups, and activities will be preserved on the kept contact.
                </p>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-500 mt-0.5" />
                <p>
                  This cannot be undone in bulk. Each merge can be individually undone from Merge History.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleStart} disabled={filteredPairs.length === 0}>
                Start Auto-Merge
              </Button>
            </DialogFooter>
          </>
        )}

        {state === 'executing' && (
          <>
            <DialogHeader>
              <DialogTitle>Auto-Merging...</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Progress value={progressPercent} className="h-2" />
                <p className="text-sm text-muted-foreground text-center">
                  {progress} of {total}
                </p>
              </div>

              {/* Log */}
              <ScrollArea className="h-40 rounded-md border p-3">
                <div className="space-y-1.5 text-sm">
                  {visibleLog.map((entry, i) => (
                    <div key={i} className="flex items-start gap-2">
                      {entry.status === 'success' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                      )}
                      <span className={entry.status === 'error' ? 'text-destructive' : ''}>
                        {entry.status === 'success'
                          ? `Merged ${entry.keepName} + ${entry.discardName}`
                          : `Failed: ${entry.keepName} + ${entry.discardName}`}
                      </span>
                    </div>
                  ))}
                  {progress < total && !cancelledRef.current && (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                      <span className="text-muted-foreground">Processing...</span>
                    </div>
                  )}
                </div>
              </ScrollArea>

              {errorCount > 0 && (
                <p className="text-sm text-destructive">
                  Errors: {errorCount}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </DialogFooter>
          </>
        )}

        {state === 'complete' && (
          <>
            <DialogHeader>
              <DialogTitle>Auto-Merge Complete</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {successCount > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                  <span>{successCount} pair{successCount !== 1 ? 's' : ''} merged successfully</span>
                </div>
              )}
              {errorCount > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <XCircle className="h-5 w-5 text-destructive shrink-0" />
                  <span>{errorCount} pair{errorCount !== 1 ? 's' : ''} failed</span>
                </div>
              )}
              {cancelledRef.current && (
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
                  <span>Cancelled by user. {total - progress} pair{total - progress !== 1 ? 's' : ''} were not processed.</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Each merge can be individually undone from the Merge History tab within 30 days.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleViewMergeHistory}>
                View Merge History
              </Button>
              <Button onClick={handleClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
